import useMediaQueries from 'hooks/common/useMediaQueries';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  PieLabelRenderProps,
} from 'recharts';

import { Card, CardContent, CardHeader, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';

interface SourceData {
  name: string;
  value: number;
  color: string;
}

interface DashboardAnalyticsItem {
  key?: string;
  value?: number;
  slug?: string;
  __typename?: string;
}

interface SourcesProps {
  data?: SourceData[] | (DashboardAnalyticsItem | undefined)[];
  isLoading?: boolean;
}

const useStyles = makeStyles((theme) => ({
  button: {
    height: '30px',
    padding: '0 8px',
    marginLeft: 'auto',
  },
  chartContainer: {
    display: 'flex',
    height: 220,
    [theme.breakpoints.down('md')]: {
      height: 180,
      flexDirection: 'column',
      alignItems: 'center',
    },
    [theme.breakpoints.down('sm')]: {
      height: 160,
    },
  },
  chartSection: {
    width: '50%',
    height: '100%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      height: '60%',
      minHeight: 120,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 100,
    },
  },
  legendContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    width: '50%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      paddingLeft: 0,
      marginTop: theme.spacing(1),
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0.5),
    },
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(1),
      padding: theme.spacing(0.5),
      minWidth: 'auto',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.5),
    },
  },
  legendColor: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      width: 8,
      height: 8,
      marginRight: theme.spacing(0.5),
    },
  },
  percentage: {
    marginLeft: 'auto',
    [theme.breakpoints.down('md')]: {
      marginLeft: theme.spacing(0.5),
    },
  },
}));

const renderCustomizedLabel = (props: PieLabelRenderProps) => {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = props;
  // Convert possible string values to numbers with fallbacks
  const numCx = typeof cx === 'number' ? cx : 0;
  const numCy = typeof cy === 'number' ? cy : 0;
  const numMidAngle = typeof midAngle === 'number' ? midAngle : 0;
  const numInnerRadius = typeof innerRadius === 'number' ? innerRadius : 0;
  const numOuterRadius = typeof outerRadius === 'number' ? outerRadius : 0;
  const numPercent = typeof percent === 'number' ? percent : 0;

  const radius = numInnerRadius + (numOuterRadius - numInnerRadius) * 0.5;
  const x = numCx + radius * Math.cos((-numMidAngle * Math.PI) / 180);
  const y = numCy + radius * Math.sin((-numMidAngle * Math.PI) / 180);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={12}
      fontWeight="bold"
    >
      {`${(numPercent * 100).toFixed(0)}%`}
    </text>
  );
};

// Map of source keys/enums to user-friendly texts
const SOURCE_LABELS: Record<string, string> = {
  CLIENT_WEBSITE: 'Client Website',
  EMPLOYEE_REFERRAL: 'Employee Referral',
  OTHER: 'Other',
  SEARCH_ENGINE: 'Search Engine',
};

const Sources: React.FC<SourcesProps> = ({ data = [], isLoading = false }) => {
  const classes = useStyles();
  const { t } = useTranslation('dashboard');
  const { isXS, isSM } = useMediaQueries();

  // Color palette for the chart
  const sourceColors = [
    '#2196F3', // blue
    '#FF5722', // orange
    '#FFC107', // yellow
    '#4CAF50', // green
    '#009688', // teal
    '#9C27B0', // purple
    '#E91E63', // pink
    '#607D8B', // blue grey
    '#795548', // brown
    '#00BCD4', // cyan
  ];

  // Map raw analytics data to SourceData if needed
  let chartData: SourceData[] = [];
  if (
    data.length > 0 &&
    typeof (data[0] as SourceData).color === 'string' &&
    typeof (data[0] as SourceData).name === 'string'
  ) {
    chartData = (data as SourceData[]).map((item) => ({
      ...item,
      name: SOURCE_LABELS[item.name] || item.name,
    }));
  } else {
    chartData = (data as (DashboardAnalyticsItem | undefined)[])
      .filter(
        (item): item is DashboardAnalyticsItem =>
          !!item && typeof item.value === 'number' && !!item.key,
      )
      .map((item, idx) => ({
        name: SOURCE_LABELS[item.key!] || item.slug || item.key!,
        value: item.value!,
        color: sourceColors[idx % sourceColors.length],
      }));
  }

  // Responsive chart dimensions
  const chartRadius = isXS ? 60 : isSM ? 70 : 100;
  const innerRadius = isXS ? 30 : isSM ? 35 : 50;

  return (
    <Card elevation={0}>
      <CardHeader
        title={<Typography variant="h6">{t('v2.sources')}</Typography>}
      />
      <CardContent>
        <Box className={classes.chartContainer}>
          <ResponsiveContainer className={classes.chartSection}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={chartRadius}
                innerRadius={innerRadius}
                paddingAngle={0}
                dataKey="value"
                strokeWidth={1}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => `${value}%`} />
            </PieChart>
          </ResponsiveContainer>
          <Box className={classes.legendContainer}>
            {chartData.map((entry, index) => (
              <Box key={`legend-${index}`} className={classes.legendItem}>
                <Box
                  className={classes.legendColor}
                  style={{ backgroundColor: entry.color }}
                />
                <Typography variant={isXS ? 'caption' : 'body2'}>
                  {entry.name}
                </Typography>
                <Typography
                  variant={isXS ? 'caption' : 'body2'}
                  className={classes.percentage}
                >
                  {entry.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default Sources;
